import { useCallback, useEffect, useRef } from 'react';
import socketClient from 'socket.io-client';

import { useAppContext } from '../../components/app-factory/context';

type ClientSocket = typeof socketClient.Socket;

const transports = ['websocket', 'xhr-polling', 'jsonp-polling', 'polling'];

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface SocketEventHandlers {
    [key: string]: (...args: any[]) => void;
}

// uri: неймспейс сокета
// eventHandlers: { имя события (emit): хенлер события }
export function useSocketIO(uri: string, eventHandlers?: SocketEventHandlers) {
    const { socketIO: settings, websocketDomain } = useAppContext();

    const uriRef = useRef(uri);
    if (uri !== uriRef.current) {
        throw Error('Only constant uri is supported');
    }

    const eventHandlersRef = useRef(eventHandlers);
    useEffect(() => {
        eventHandlersRef.current = eventHandlers;
    }, [eventHandlers]);

    const socketRef = useRef<ClientSocket>();
    useEffect(() => {
        const socket = socketClient(`${websocketDomain}${uri}`, {
            ...settings.clientOptions,
            path: settings.mountPath,
            transports,
        });
        socketRef.current = socket;

        if (eventHandlersRef.current) {
            Object.keys(eventHandlersRef.current).forEach((event) => {
                socket.on(event, (...args: any[]) => {
                    eventHandlersRef.current?.[event](...args);
                });
            });
        }

        return () => {
            // Чтобы компонент мог что-то успеть отправить перед закрытием сокета
            setTimeout(() => socket.close(), 0);
        };
    }, [settings, websocketDomain, uri]);

    const emit = useCallback(function (event: string, ...args: any[]) {
        socketRef.current?.emit(event, ...args);
    }, []);

    return { emit };
}
